/**
 * CustomDocHandler — v238.3.0
 *
 * Self-contained patient Custom Document Handler. Everything related to a
 * patient-requested document lives here:
 *   1. Request   — upload a blank form + a note, submit it to the doctor.
 *   2. Track     — see each request's status (with your doctor / ready).
 *   3. Receive   — view / download the finished signed PDF once it's sent back.
 *
 * Opened as a portal modal from the case (CaseHome header). No state leaks into
 * the surrounding screen — the parent only mounts/unmounts it.
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Upload, FileText, Clock, CheckCircle, Download, Plus, AlertCircle, ShieldCheck, ArrowLeft, Send } from 'lucide-react'
import { customDocAPI } from '../services/api'
import { useI18n } from '../i18n'
import { fitsGatewayAsMultipart } from '../utils/uploadLimit'
import { clinicianName } from '../utils/clinicianName.js'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE = '#E9F8FF'
const LIGHT = '#A7D9FC'
const GREEN = '#66D170'
const GREEN_DARK = '#16A34A'

// PROD-791: the bytes go through API Gateway -> Lambda (6 MB request quota; base64 4/3), so ~4.5 MB is the real ceiling; Alex 2026-10-09: "max 4 MB".
const MAX_BYTES = 4 * 1024 * 1024
const ACCEPT = 'application/pdf,image/png,image/jpeg'

function stripExt(name) {
  if (!name) return ''
  const i = name.lastIndexOf('.')
  return i > 0 ? name.slice(0, i) : name
}

// Patient-facing status: a doc is either still with the doctor, or ready.
function statusView(status) {
  if (status === 'sent') return { label: 'Ready', tone: 'ready' }
  return { label: 'With your doctor', tone: 'pending' }
}

export default function CustomDocHandler({ caseId, doctor, onClose }) {
  const { t } = useI18n()
  const [visible, setVisible] = useState(false)
  const [view, setView] = useState('list') // 'list' | 'request'
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // request-form state
  const [file, setFile] = useState(null)
  const [uploadMeta, setUploadMeta] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [formName, setFormName] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
  }, [])

  const close = () => {
    setVisible(false)
    setTimeout(onClose, 280)
  }

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await customDocAPI.listRequests(caseId)
      const list = (res?.caseRequests || []).filter(r => r.docType === 'custom')
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      setDocs(list)
    } catch (_e) {
      setError('Could not load your documents.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (caseId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId])

  const doctorName = doctor?.firstName && doctor?.lastName
    ? clinicianName(doctor)
    : (doctor?.name || 'your doctor')

  const onPickFile = async (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > MAX_BYTES) {
      setError(t('customDoc.fileTooLarge')) // PROD-808: was a hard-coded English literal
      return
    }
    // ★ PROD-228: the API takes 20 MB here, but the request reaches it as a Lambda event
    //   (6 MB quota; the multipart body arrives base64-encoded, 4/3 of it), so a file under
    //   the size above can still be over it; AWS refuses that with no CORS (a network error).
    if (!fitsGatewayAsMultipart(f)) {
      setError(t('detect.uploadFailedTooLarge', { name: f.name, defaultValue: '"{{name}}" is too large to upload.' }))
      return
    }
    setError('')
    setUploading(true)
    setFile(f)
    try {
      const meta = await customDocAPI.patientUpload(caseId, f)
      if (!meta?.success) throw new Error(meta?.error || 'Upload failed')
      setUploadMeta(meta)
      if (!formName) setFormName(stripExt(meta.fileName || f.name))
    } catch (err) {
      setError(err?.message || 'Upload failed. Please try again.')
      setFile(null)
    } finally {
      setUploading(false)
    }
  }

  const clearUpload = () => {
    setFile(null)
    setUploadMeta(null)
  }

  const submit = async () => {
    if (!uploadMeta) {
      setError('Please upload a form first.')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const config = {
        originalFileUrl: uploadMeta.storagePath,
        originalFileName: uploadMeta.fileName,
        originalFileHash: uploadMeta.fileHash,
        contentType: uploadMeta.contentType,
        extension: uploadMeta.extension,
        pageCount: uploadMeta.pageCount,
        hasAcroForm: !!uploadMeta.hasAcroForm,
        fileSize: uploadMeta.fileSize,
        formFields: Array.isArray(uploadMeta.fields) ? uploadMeta.fields : [],
        formName: formName || stripExt(uploadMeta.fileName || ''),
        patientNote: note || '',
      }
      const title = formName || stripExt(uploadMeta.fileName || '') || 'Custom document'
      await customDocAPI.createRequest(caseId, { config, patientNote: note || '', title })
      setFile(null)
      setUploadMeta(null)
      setFormName('')
      setNote('')
      setView('list')
      await load()
    } catch (err) {
      setError(err?.message || 'Could not submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const download = async (doc) => {
    if (!doc.pdfUrl) return
    try {
      const res = await customDocAPI.signUrl(caseId, doc.pdfUrl)
      if (res?.signedUrl) window.open(res.signedUrl, '_blank', 'noopener,noreferrer')
      else throw new Error('No URL')
    } catch (_e) {
      setError('Could not open the document. Please try again.')
    }
  }

  // ── Render ────────────────────────────────────────────────────────────
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      style={{
        backgroundColor: visible ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0)',
        transition: 'background-color 280ms ease',
      }}
      onClick={close}
    >
      <div
        className="w-full sm:max-w-md bg-white sm:rounded-2xl rounded-t-2xl overflow-hidden flex flex-col bottom-actions [--ba-min:0px]"
        style={{
          maxHeight: '88vh',
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          opacity: visible ? 1 : 0,
          transition: 'transform 280ms ease, opacity 280ms ease',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5" style={{ backgroundColor: NAVY }}>
          <div className="flex items-center gap-2.5">
            {view === 'request' && (
              <button onClick={() => { setView('list'); setError('') }} className="text-white/70 hover:text-white">
                <ArrowLeft size={20} />
              </button>
            )}
            <div className="flex items-center gap-2">
              <FileText size={17} className="text-white" />
              <span className="text-[15px] font-bold text-white">
                {view === 'request' ? 'Request a document' : 'Documents'}
              </span>
            </div>
          </div>
          <button onClick={close} className="text-white/70 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-3 rounded-xl p-3 flex items-start gap-2" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#DC2626' }} />
              <p className="text-[12px] m-0" style={{ color: '#991B1B' }}>{error}</p>
            </div>
          )}

          {view === 'list' && (
            <>
              <p className="text-[13px] leading-relaxed mb-3" style={{ color: '#475569' }}>
                {t('customDoc.intro', { doctor: doctorName, defaultValue: 'Need a form filled out? Upload it here and {{doctor}} will complete and sign it for you.' })}
              </p>

              <button
                onClick={() => { setView('request'); setError('') }}
                className="w-full flex items-center justify-center gap-2 rounded-xl py-3 mb-4 font-semibold text-white text-[14px]"
                style={{ backgroundColor: NAVY }}
              >
                <Plus size={16} />
                {t('customDoc.requestADocument', 'Request a document')}
              </button>

              {loading ? (
                <div className="flex items-center justify-center py-8 gap-2 text-[13px]" style={{ color: BLUE }}>
                  <OrellaSpinner size={16} tone="inherit" />
                  {t('common.loadingEllipsis', 'Loading…')}
                </div>
              ) : docs.length === 0 ? (
                <div className="text-center py-8">
                  <FileText size={28} className="mx-auto mb-2" style={{ color: LIGHT }} />
                  <p className="text-[13px] m-0" style={{ color: '#64748B' }}>{t('customDoc.noDocumentsYet')}</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {docs.map((doc) => {
                    const sv = statusView(doc.status)
                    const ready = sv.tone === 'ready'
                    return (
                      <div
                        key={doc.id}
                        className="rounded-xl p-3"
                        style={{
                          border: `1px solid ${ready ? '#C7EAD0' : '#E2E8F0'}`,
                          backgroundColor: ready ? '#F3FCF5' : 'white',
                        }}
                      >
                        <div className="flex items-center gap-2.5">
                          <span
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: ready ? GREEN : '#F1F5F9' }}
                          >
                            {ready
                              ? <CheckCircle size={16} className="text-white" />
                              : <Clock size={16} style={{ color: '#94A3B8' }} />}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold m-0 truncate" style={{ color: NAVY }}>
                              {doc.title || doc.config?.formName || 'Custom document'}
                            </p>
                            <p className="text-[11px] m-0" style={{ color: ready ? GREEN_DARK : '#64748B' }}>
                              {sv.label}
                            </p>
                          </div>
                          {ready && doc.pdfUrl && (
                            <button
                              onClick={() => download(doc)}
                              className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-semibold text-white flex-shrink-0"
                              style={{ backgroundColor: NAVY }}
                            >
                              <Download size={13} />
                              {t('customDoc.open', 'Open')}
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}

          {view === 'request' && (
            <div className="space-y-4">
              {/* HIPAA note */}
              <div className="rounded-xl p-3 flex items-start gap-2" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA' }}>
                <ShieldCheck size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#C2410C' }} />
                <p className="text-[11px] leading-relaxed m-0" style={{ color: '#9A3412' }}>
                  {/* ★ The emphasised word is INSIDE the sentence, so the placeholder is left
                      unresolved and split on: Spanish does not put "blank" where English does. */}
                  {t('customDoc.blankOnly', { defaultValue: 'Upload a {{blank}} form only — one that doesn’t already contain someone else’s information.' })
                    .split('{{blank}}')
                    .flatMap((part, i) => i === 0 ? [part] : [
                      <span key={`b${i}`} className="font-semibold">{t('customDoc.blankWord', 'blank')}</span>, part,
                    ])}
                </p>
              </div>

              {/* Upload */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>
                  {t('customDoc.blankForm', 'Blank form')}
                </label>
                {!uploadMeta ? (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full rounded-xl py-6 flex flex-col items-center justify-center gap-1.5 disabled:opacity-60"
                    style={{ border: `1.5px dashed ${LIGHT}`, backgroundColor: '#F8FCFF' }}
                  >
                    {uploading ? (
                      <>
                        <OrellaSpinner size={20} />
                        <span className="text-[12px]" style={{ color: BLUE }}>{t('customDoc.uploading', 'Uploading…')}</span>
                      </>
                    ) : (
                      <>
                        <Upload size={22} style={{ color: BLUE }} />
                        <span className="text-[13px] font-semibold" style={{ color: NAVY }}>{t('customDoc.uploadFileTypes', 'Upload PDF, PNG, or JPG')}</span>
                        <span className="text-[11px]" style={{ color: '#94A3B8' }}>{t('customDoc.tapToChooseMax')}</span>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="rounded-xl p-3 flex items-center gap-2.5" style={{ border: `1px solid ${LIGHT}`, backgroundColor: '#F8FCFF' }}>
                    <FileText size={20} style={{ color: BLUE }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold m-0 truncate" style={{ color: NAVY }}>{uploadMeta.fileName}</p>
                      <p className="text-[10px] m-0" style={{ color: '#64748B' }}>
                        {uploadMeta.pageCount || 1} page{(uploadMeta.pageCount || 1) === 1 ? '' : 's'}
                      </p>
                    </div>
                    <button onClick={clearUpload} className="text-slate-400 hover:text-red-500">
                      <X size={16} />
                    </button>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept={ACCEPT} className="hidden" onChange={onPickFile} />
              </div>

              {/* Form name */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>
                  {t('customDoc.whatIsIt', 'What is it?')}
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Work excuse note"
                  className="w-full h-10 px-3 text-[14px] rounded-xl outline-none"
                  style={{ border: '1.5px solid #E2E8F0', color: NAVY }}
                />
              </div>

              {/* Note */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>
                  {t('customDoc.whatDoYouNeed', 'What do you need?')} <span className="font-normal normal-case tracking-normal" style={{ color: '#94A3B8' }}>(optional)</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. Out sick Mon–Wed, need a work excuse note."
                  className="w-full p-3 text-[13px] rounded-xl outline-none leading-relaxed"
                  style={{ border: '1.5px solid #E2E8F0', color: NAVY, resize: 'vertical' }}
                />
              </div>

              <button
                onClick={submit}
                disabled={!uploadMeta || submitting}
                className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-white text-[14px] disabled:opacity-50"
                style={{ backgroundColor: NAVY }}
              >
                {submitting ? <OrellaSpinner size={16} tone="inherit" /> : <Send size={15} />}
                {submitting ? 'Sending…' : `Send to ${doctorName}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
